import React, { Component } from 'react'
import { IntlContext } from 'react-intl'
import PropTypes from 'prop-types'
import moment from 'moment'

import enLocaleData from '../locale-data/en'
import svLocaleData from '../locale-data/sv'

import { invariantIntlContext } from '../utils'

const localeData = Object.fromEntries([...enLocaleData, ...svLocaleData].map(data => [data.locale, data]))

function findFields(locale) {
  let data = localeData[locale.toLowerCase()]

  // The locale data is de-duplicated, so we have to traverse the locale's
  // hierarchy until we find `fields` to return.
  while (data) {
    if (data.fields) {
      return data.fields
    }

    data = data.parentLocale && localeData[data.parentLocale.toLowerCase()]
  }

  throw new Error('Locale data added is missing `fields` for :' + locale)
}

function formatRelative(config, value) {
  const startOfYear = moment()
    .subtract(1, 'day')
    .startOf('year')
  const startOfYesterday = moment()
    .subtract(1, 'day')
    .startOf('day')
  const startOfDay = moment().startOf('day')
  const endOfDay = moment().endOf('day')
  const endOfTomorrow = moment()
    .add(1, 'day')
    .endOf('day')
  const endOfYear = moment()
    .add(1, 'day')
    .endOf('year')
  const date = moment(value)

  const fields = findFields(config.locale)

  if (date.isBetween(startOfDay, endOfDay)) {
    return config.formatTime(value)
  }

  if (date.isBetween(endOfDay, endOfTomorrow)) {
    return `${fields.day.relative[1]} ${config.formatTime(value)}`
  }

  if (date.isBetween(startOfYesterday, startOfDay)) {
    return `${fields.day.relative[-1]} ${config.formatTime(value)}`
  }

  if (date.isBetween(startOfYear, endOfYear)) {
    return `${config.formatDate(value, {
      month: 'long',
      day: 'numeric',
    })} ${config.formatTime(value)}`
  }

  return `${config.formatDate(value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })} ${config.formatTime(value)}`
}

export default class FormattedRelative extends Component {
  static propTypes = {
    value: PropTypes.any.isRequired,
  }

  componentDidMount() {
    this.scheduleNextUpdate()
  }

  componentWillUnmount() {
    clearTimeout(this._timeout)
  }

  scheduleNextUpdate() {
    // next 'interesting' moment
    const delay = moment()
      .endOf('day')
      .diff(moment())

    this._timeout = setTimeout(() => {
      this.scheduleNextUpdate()
    }, delay)
  }

  render() {
    return (
      <IntlContext.Consumer>
        {intl => {
          invariantIntlContext(intl)
          const { textComponent: Text } = intl
          const { value } = this.props

          return <Text>{formatRelative(intl, value)}</Text>
        }}
      </IntlContext.Consumer>
    )
  }
}
